import { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';
import StatusPill from './StatusPill.jsx';
import { getCategoryColorClass } from '../constants';
import { catalogCategoryLabel } from '../utils';
import { mapAttentionItemToUi } from '../utils/attentionMappers.js';
import { useIntegrationsPermissions } from '../hooks';
import { ChevronDown, ChevronRight } from 'lucide-react';

/** Prototype ATTN_CAP — collapsed list shows this many rows. */
const MAX_VISIBLE = 5;

/**
 * Stable per-person avatar washes (prototype `.op-row-avatar` family). Tailwind
 * arbitrary gradients so we stay off inline `style`.
 */
const AVATAR_GRADIENT_CLASSES = [
  'bg-[linear-gradient(135deg,#06B6D4,#A78BFA)]',
  'bg-[linear-gradient(135deg,#FDC700,#EC4899)]',
  'bg-[linear-gradient(135deg,#05DF72,#2B7FFF)]',
  'bg-[linear-gradient(135deg,#8B5CF6,#EC4899)]',
  'bg-[linear-gradient(135deg,#2B7FFF,#06B6D4)]',
];

const gradientClassFor = (seed = '') => {
  let sum = 0;
  for (let i = 0; i < seed.length; i += 1) sum += seed.charCodeAt(i);
  return AVATAR_GRADIENT_CLASSES[sum % AVATAR_GRADIENT_CLASSES.length];
};

/**
 * Needs Your Attention band (CT-4159, canonical — supersedes the duplicate
 * CT-4196): summary of every integration/instance that is not Working. Caps at
 * 5 rows; "Show all N +overflow" expands in-band with an internal scroll
 * (`scrollbar-aimsos-auto-sm`, max-height ~5.5 rows).
 *
 * Admin-gated (CT-4159): renders only when `canManageIntegrations` is true AND
 * there is at least one item — otherwise it is ABSENT from the DOM (not merely
 * hidden). RBAC is stubbed to `true` in V1 (real gating: CT-3900 / ARP-804).
 * Items arrive already severity-sorted from `useIntegrationsHome` (CT-4195
 * `SEVERITY_ORDER`).
 */
function NeedsAttentionBand({ items = [] }) {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { canManageIntegrations } = useIntegrationsPermissions();
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => items.map(mapAttentionItemToUi), [items]);

  if (!canManageIntegrations || rows.length === 0) return null;

  const overflow = rows.length - MAX_VISIBLE;
  const canExpand = overflow > 0;
  const visible = expanded || !canExpand ? rows : rows.slice(0, MAX_VISIBLE);

  return (
    <section
      aria-label={t('integrations.attention.heading')}
      className={[
        'rounded-[10px] border border-[rgba(251,191,36,0.22)] border-l-2 border-l-[#FDC700]',
        'bg-[rgba(251,191,36,0.04)] p-[18px]',
      ].join(' ')}
    >
      <header className="mb-[14px] flex items-baseline gap-2.5">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#FDC700]">
          <span
            aria-hidden="true"
            className="h-[7px] w-[7px] animate-pulse rounded-full bg-[#FDC700]"
          />
          {t('integrations.attention.heading')}
        </span>
        <span className="text-[11px] font-medium text-content-faint">
          {t('integrations.attention.count', { count: rows.length })}
        </span>
      </header>

      <ul
        className={[
          'flex flex-col gap-1.5',
          expanded && canExpand
            ? 'max-h-[408px] overflow-y-auto pr-1.5 scrollbar-aimsos-auto-sm'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {visible.map((row) => (
          <li key={row.id}>
            {/*
              Row click is a mouse-only convenience (matches the prototype's
              `.op-row` onclick). The CTA button below is the real,
              keyboard-accessible navigation control — so we don't give the row
              an interactive role/tabIndex and nest a <button> inside a link.
            */}
            <div
              onClick={() => navigate(row.fixHref)}
              className="flex cursor-pointer items-center justify-between gap-4 rounded-[10px] border border-edge bg-surface px-4 py-3 transition-colors hover:border-edge-strong hover:bg-surface-hover-subtle"
            >
              <div className="flex min-w-0 items-center gap-3">
                <IntegrationProviderLogo slug={row.slug} name={row.name} size="small" />
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold leading-tight text-content">
                    {row.instanceName ? `${row.name} — ${row.instanceName}` : row.name}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-[12px] font-medium text-content-faint">
                    <span className={getCategoryColorClass(row.category) || undefined}>
                      {catalogCategoryLabel(row.category, t)}
                    </span>
                    {row.callsPerDay > 0 && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>
                          {t('integrations.attention.callsPerDay', {
                            value: row.callsPerDay.toLocaleString(),
                          })}
                        </span>
                      </>
                    )}
                    {row.usedInWorkflows > 0 && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>
                          {t(
                            row.usedInWorkflows === 1
                              ? 'integrations.attention.usedInWorkflow'
                              : 'integrations.attention.usedInWorkflows',
                            { count: row.usedInWorkflows },
                          )}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2.5">
                <Button
                  variant="tertiary"
                  size="sm"
                  rightIcon={ChevronRight}
                  customIconSize={14}
                  customFontSize="text-[12px]"
                  customButtonHeight="!h-[30px]"
                  className="!border-[#FDC700] !bg-[#FDC700] !text-[#1a0f00] hover:!bg-[#e3b400]"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(row.fixHref);
                  }}
                >
                  {row.ctaLabelKey ? t(row.ctaLabelKey) : t('integrations.attention.fixNow')}
                </Button>

                <span aria-hidden="true" className="h-[18px] w-px bg-edge" />

                <StatusPill status={row.status} />

                {row.connectedBy?.initials && (
                  <Avatar
                    alt={row.connectedBy.name || row.connectedBy.initials}
                    initials={row.connectedBy.initials}
                    className={[
                      '!h-[26px] !w-[26px] !min-h-[26px] !min-w-[26px] !border-0',
                      'text-white [&_span]:text-[10px] [&_span]:font-extrabold [&_span]:tracking-[-0.02em]',
                      gradientClassFor(row.connectedBy.initials),
                    ].join(' ')}
                  />
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {canExpand && (
        <Button
          type="button"
          variant="tertiary"
          size="md"
          rightIcon={ChevronDown}
          className={[
            'mt-3 !rounded-[8px] !border !border-edge !bg-transparent !font-semibold !text-content-secondary',
            'hover:!border-edge-strong hover:!bg-white/[0.04] hover:!text-content',
            expanded ? '[&_svg]:rotate-180' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? (
            t('integrations.attention.showLess')
          ) : (
            <>
              {t('integrations.attention.showAll', { count: rows.length })}
              <span className="ml-0.5 text-[11px] font-bold text-[#FDC700]">+{overflow}</span>
            </>
          )}
        </Button>
      )}
    </section>
  );
}

export default memo(NeedsAttentionBand);
